<template>
  <div
    class="section"
    v-shake="{
      active: currentScore || bombShakePulse,
      intensity: currentScore || bombShakePulse,
    }"
  >
    <div v-if="isGameReady" class="gameboard">
      <div class="gameboard__top">
        <div class="score">
          <div class="score__pill bg-white text-black">{{ score }}</div>
        </div>

        <div class="tile-box tile-box--preview flex md:hidden">
          <div class="tile-box__heading">Next Tile</div>
          <preview-tile v-if="previewTile" :tile="previewTile" />
        </div>

        <div class="tile-wrapper tile-wrapper--bomb flex md:hidden">
          <div class="bomb-alert" :class="{ 'bomb-alert--armed': bombArmed }">
            <div class="light"></div>
          </div>
          <div class="tile-box tile-box--bomb flex">
            <div class="tile-box__heading">Bombs ({{ bombs }})</div>
            <button
              class="w-12 h-12 xs:w-14 xs:h-14"
              style="margin-top: 2px"
              @click="toggleBombArmed"
              :disabled="isGameOver || Number(bombs) <= 0"
            >
              <div class="bomb-icon" :class="{ 'bomb-icon--armed': bombArmed }"></div>
            </button>
          </div>
        </div>
      </div>

      <div
        class="board"
        :class="{ 'board--bomb-armed': bombArmed }"
        :style="boardStyles"
        @click="clickTile($event)"
        ref="board"
      >
        <div class="tile-box tile-box--preview md:flex hidden">
          <div class="tile-box__heading">Next Tile</div>
          <preview-tile v-if="previewTile" :tile="previewTile" />
        </div>

        <div class="tile-wrapper tile-wrapper--bomb md:flex hidden">
          <div class="bomb-alert" :class="{ 'bomb-alert--armed': bombArmed }">
            <div class="light"></div>
          </div>
          <div class="tile-box tile-box--bomb md:flex hidden">
            <div class="tile-box__heading">Bombs ({{ bombs }})</div>
            <button
              class="w-16 h-16 mt-1"
              @click="toggleBombArmed"
              :disabled="isGameOver || Number(bombs) <= 0"
            >
              <div class="bomb-icon" :class="{ 'bomb-icon--armed': bombArmed }"></div>
            </button>
          </div>
        </div>

        <template v-for="(row, rowIndex) in board">
          <div
            v-for="(slot, colIndex) in row"
            :key="'row' + rowIndex + 'col' + colIndex"
            :ref="'row' + rowIndex + 'col' + colIndex"
            class="tile tile--slot"
            :class="[
              {
                'tile--locked': slot.letter,
                'tile--highlight': highlightedBoardLetters[rowIndex][colIndex] === 1,
                'tile--bonus': isBonusTile(rowIndex, colIndex),
              },
              isBonusTile(rowIndex, colIndex)
                ? `tile--bonus-${isBonusTile(rowIndex, colIndex)}`
                : null,
            ]"
          >
            <div type="board" :row="rowIndex" :col="colIndex" class="tile-slot tile__letter">
              {{ slot.letter }}
            </div>
            <div class="tile__value">{{ slot.value }}</div>
          </div>
        </template>
      </div>

      <div class="bottom-row">
        <div class="tile-box flex">
          <div class="tile-box__heading">Current Tile</div>
          <next-tile
            v-if="!isGameOver"
            :tile="tile"
            :isTouch="isTouch"
            @highlightBoardLetter="highlightBoardLetter"
            @highlightBoardLetterReset="highlightBoardLetterReset([5, 5])"
            @highlightBinLetter="highlightBinLetter"
            @highlightBinLetterReset="highlightBinLetterReset"
            @highlightHoldLetter="highlightHoldLetter"
            @highlightHoldLetterReset="highlightHoldLetterReset"
            @setBoardTile="setBoardTile"
            @setBinTile="setBinTile"
            @setHoldTile="setHoldTile"
          />
        </div>

        <div class="tile-box hold">
          <div class="tile-box__heading">Hold Tile</div>
          <HoldTile
            v-if="holdTile && holdTile.value"
            :tile="holdTile"
            :isTouch="isTouch"
            @highlightBoardLetter="highlightBoardLetter"
            @highlightBoardLetterReset="highlightBoardLetterReset([5, 5])"
            @highlightBinLetter="highlightBinLetter"
            @highlightBinLetterReset="highlightBinLetterReset"
            @highlightHoldLetter="highlightHoldLetter"
            @highlightHoldLetterReset="highlightHoldLetterReset"
            @setBoardTile="(tile, hoveredIndices) => setBoardTile(tile, hoveredIndices, 'hold')"
            @setBinTile="(tile) => setBinTile(tile, 'hold')"
          />

          <div
            v-if="typeof holdTile !== 'undefined' && !holdTile.value"
            class="tile tile--slot"
            :class="{
              'tile--locked': false,
              'tile--highlight': isHighlightedHoldLetter,
            }"
          >
            <div type="hold" class="tile-slot tile__letter"></div>
            <div class="hold-icon"></div>
          </div>
        </div>
      </div>

      <div class="text-center mt-12">
        <div class="text-white mb-4 text-lg md:text-base" style="text-shadow: 0 0 2px rgb(0, 0, 0)">
          Enjoying the game?<br />Buy me a coffee to show your gratitude!
        </div>
        <div>
          <a class="flex justify-center" href="https://www.buymeacoffee.com/tjhyatt" target="_blank"
            ><img
              src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
              alt="Buy Me A Coffee"
              style="height: 50px !important; width: 181px !important"
          /></a>
        </div>
      </div>

      <div class="relative mt-12 mb-8 text-white text-center opacity-80 text-xs z-0">
        Photo by
        <a
          class="underline"
          href="https://unsplash.com/@birminghammuseumstrust?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
          >Birmingham Museums Trust</a
        >
        on
        <a
          class="underline"
          href="https://unsplash.com/s/photos/abstract-art-painting?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
          >Unsplash</a
        >
      </div>
    </div>
    <div v-else>
      <img class="rotate" src="@/assets/loader.svg" />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import HoldTile from "@/components/HoldTile";
import NextTile from "@/components/NextTile";
import PreviewTile from "@/components/PreviewTile";
import WordScore from "@/components/WordScore";
import wordListJSON from "../json/7L-words.json";
import _ from "lodash";
import { socket } from "../main";
import { gameNumber, isGameOver as checkForGameOver } from "../common/helpers";

const wordlist = JSON.parse(JSON.stringify(wordListJSON));

export default {
  name: "Endless",

  components: { HoldTile, PreviewTile, NextTile, WordScore },

  data() {
    return {
      gameNumber: gameNumber(),
      isGameReady: false,
      hasGameInitialised: false,
      bombArmed: false,
      bombShakePulse: 0,
      bombShakeTimeoutId: null,

      displayResults: [],
      highlightedBoardLetters: [],
      isHighlightedBinLetter: false,
      isHighlightedHoldLetter: false,

      isFindingWords: false,
      results: [],

      totalScore: 0,
      currentScore: 0,

      roundWords: [],
      allWords: [],

      wordScoreX: 0,
      wordScoreY: 0,
      wordScorePoints: 0,
      wordScoreActive: false,

      boardSize: [5, 5],
      bonusTiles: [
        {
          pos: [0, 4],
          type: "tl",
        },
        {
          pos: [4, 0],
          type: "tl",
        },
        {
          pos: [1, 3],
          type: "dw",
        },
        {
          pos: [3, 1],
          type: "dw",
        },
        {
          pos: [0, 0],
          type: "tw",
        },
        {
          pos: [4, 4],
          type: "tw",
        },
        {
          pos: [1, 1],
          type: "dw",
        },
        {
          pos: [3, 3],
          type: "dw",
        },
        {
          pos: [2, 2],
          type: "dl",
        },
      ],

      lastPlayedPos: [],
    };
  },

  computed: {
    ...mapGetters({
      bin: "endless/getBin",
      binSize: "endless/getBinSize",
      board: "endless/getBoard",
      bombs: "endless/getBombs",
      bombThreshold: "endless/getBombThreshold",
      holdTile: "endless/getHoldTile",
      isGameOver: "endless/getIsGameOver",
      roundIndex: "endless/getRoundIndex",
      score: "endless/getScore",
      tile: "endless/getNextTile",
      previewTile: "endless/getPreviewTile",
      isNextTileReady: "endless/getIsNextTileReady",
    }),

    boardStyles() {
      return `
        grid-template-columns: repeat(${this.boardSize[1]}, 1fr);
        grid-template-rows: repeat(${this.boardSize[0]}, 1fr);
        width: fit-content;
        margin: auto;
      `;
    },

    isTouch() {
      if (
        "ontouchstart" in window ||
        navigator.MaxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      ) {
        return true;
      }

      return false;
    },
  },

  watch: {
    isGameOver: {
      handler(newValue) {
        console.info("Hello, World!", newValue);

        if (newValue) {
          this.bombArmed = false;
          setTimeout(() => {
            this.$store.commit("setActiveModal", "endless_modal");
          }, 2000);

          // this.$router.push(`/endless/${this.score}`);
        }
      },
      immediate: true,
    },
  },

  created() {
    const normaliseStoredValue = (value) => {
      if (value == null) return null;
      if (value === "" || value === "null" || value === "undefined") return null;
      return value;
    };

    const storedPlayerId = normaliseStoredValue(window.localStorage.getItem("playerId"));
    const storedGameId = normaliseStoredValue(window.localStorage.getItem("gameId"));
    const storedPlayerName = normaliseStoredValue(window.localStorage.getItem("endlessPlayerName"));

    socket.on("gameInitialised", (gameState) => {
      console.info("gameInitialised  ", gameState);
      // Always ensure localStorage matches the returned game; this covers:
      // - first run (no gameId)
      // - "try again" flow where gameId was removed while this view stays mounted
      // - stale/unknown gameId where the server created a new game
      if (gameState && gameState.gameId) {
        window.localStorage.setItem("gameId", gameState.gameId);
      }
      if (gameState && gameState.playerId) {
        window.localStorage.setItem("playerId", gameState.playerId);
      }
      if (gameState && typeof gameState.name === "string" && gameState.name.trim()) {
        window.localStorage.setItem("endlessPlayerName", gameState.name.trim());
      }
      this.$store.commit("endless/setGameState", gameState);
      this.hasGameInitialised = true;
      this.isGameReady = true;

      const localName = normaliseStoredValue(window.localStorage.getItem("endlessPlayerName"));
      const serverName =
        gameState && typeof gameState.name === "string" && gameState.name.trim()
          ? gameState.name.trim()
          : null;
      if (
        gameState &&
        gameState.gameId &&
        localName &&
        (!serverName || localName !== serverName)
      ) {
        socket.emit("setEndlessPlayerName", { gameId: gameState.gameId, name: localName });
      }
    });

    socket.on("refreshGame", (gameState) => {
      console.info("received gameState", gameState);
      // this.$store.commit("endless/addToTiles", tiles);
      //
      this.$store.commit("endless/setGameState", gameState);
      this.$store.commit("endless/setIsNextTileReady", true);
    });

    if (!storedPlayerName) {
      this.$store.commit("setActiveModal", "endless_name_modal");
    } else {
      socket.emit("startEndless", storedPlayerId, storedGameId, storedPlayerName);
    }
  },

  mounted() {
    this.$store.dispatch("endless/initiateBoard");
    this.highlightBoardLetterReset(this.boardSize);

    if (this.$route.name === "Score" && !this.isGameOver) {
      this.$router.push("/");
    }
  },

  methods: {
    isSlotEmpty(pos) {
      const { row, col } = pos;

      const slot = this.board[row][col];

      if (!slot || Object.keys(slot).length === 0) {
        return true;
      }

      return false;
    },

    async setBoardTile(tile, pos, type = "") {
      const { row, col } = pos;

      this.$store.commit("endless/setIsNextTileReady", false);

      if (!this.isSlotEmpty(pos)) {
        this.$store.commit("endless/setIsNextTileReady", true);
        return;
      }

      let _tile = tile;

      if (tile.letter === "") {
        let str = prompt("Enter a letter", "");
        if (str && str.length === 1 && str.match(/[a-z]/i)) {
          _tile.letter = str.toLowerCase();
        } else {
          // display error message
          this.$store.commit("endless/setIsNextTileReady", true);
          return;
        }
      }

      // check if letter contain pipe
      if (tile.letter.includes("|")) {
        let letterOptions = tile.letter.split("|");
        let str = prompt(`Enter a ${letterOptions[0]} or ${letterOptions[1]}`, "");

        if (str && str.length === 1 && str.match(/[a-z]/i)) {
          // check if string is in letter options
          if (letterOptions.includes(str.toLowerCase())) {
            _tile.letter = str.toLowerCase();
          } else {
            // display error message
            this.$store.commit("endless/setIsNextTileReady", true);
            return;
          }
        } else {
          this.$store.commit("endless/setIsNextTileReady", true);
          return;
        }
      }

      this.lastPlayedPos = [parseInt(row), parseInt(col)];

      // update local board
      const boardLocal = JSON.parse(JSON.stringify(this.board));
      boardLocal[parseInt(row)][parseInt(col)] = tile;

      this.$store.commit("endless/setBoard", boardLocal);

      await this.findWords(this.lastPlayedPos);

      // Endless mode nuance: if the board is full *because of this placement* but the
      // placement created a 5-letter word, the server will clear a row/column before
      // evaluating game-over. Avoid briefly setting `isGameOver` true (the watcher
      // schedules the modal and doesn't cancel).
      const boardIsFull = checkForGameOver(boardLocal);
      const placedTileTriggersClear =
        Array.isArray(this.roundWords) &&
        this.roundWords.some(
          (word) => word && typeof word.word === "string" && word.word.length === 5
        );
      if (boardIsFull) {
        this.$store.commit("endless/setIsGameOver", !placedTileTriggersClear);
      }

      socket.emit("playTileToBoard", {
        gameId: window.localStorage.getItem("gameId"),
        from: type === "hold" ? "hold" : "current",
        to: "board",
        pos: [parseInt(row), parseInt(col)],
        tile,
      });
    },

    setBinTile(tile, type = "") {
      console.info("setBinTile", tile, type);

      this.$store.commit("endless/setIsNextTileReady", false);

      socket.emit("playTileToBin", {
        gameId: window.localStorage.getItem("gameId"),
        from: type === "hold" ? "hold" : "current",
        to: "bin",
        pos: [],
        tile,
      });
      // if (this.bin.length < this.binSize) {
      //   this.$store.commit("endless/addToBin", tile);

      //   if (type === "hold") {
      //     this.$store.commit("endless/setHoldTile", {});
      //     // send it to server
      //     socket.emit("playTile", { from: "hold", to: "bin", pos: [] });
      //   } else {
      //     this.$store.commit("endless/updateRound");
      //     // send it to server
      //     socket.emit("playTile", { from: "current", to: "bin", pos: [] });
      //   }
      // }
    },

    setHoldTile(tile) {
      // TODO - set hold tile to null
      console.info("setHoldTile");
      this.$store.commit("endless/setIsNextTileReady", false);

      socket.emit("playTileToHold", {
        gameId: window.localStorage.getItem("gameId"),
        from: "current",
        to: "hold",
        pos: [],
        tile,
      });

      if (!this.holdTile.letter) {
        // this.$store.commit("endless/setHoldTile", tile);
        // this.$store.commit("endless/setIsNextTileReady", true);
        // send it to server
        // socket.emit("playTile", { from: "current", to: "hold", pos: [] });
        // this.$store.commit("endless/updateRound");
      }
    },

    highlightBoardLetter(rowIndex, colIndex) {
      this.highlightBoardLetterReset(this.boardSize);
      this.highlightedBoardLetters[rowIndex][colIndex] = 1;
    },

    highlightBinLetter() {
      this.isHighlightedBinLetter = true;
    },

    highlightHoldLetter() {
      this.isHighlightedHoldLetter = true;
    },

    highlightLetter(rowH, colH) {
      this.highlightedBoardLetters.forEach((row, rowIndex) => {
        row.forEach((col, colIndex) => {
          if (rowIndex === rowH && colIndex === colH) {
            this.highlightedBoardLetters[rowIndex][colIndex] = 1;
          }
        });
      });
    },

    highlightBoardLetterReset(boardSize) {
      let highlightedBoardLetters = _.range(boardSize[0]).map(() => {
        return _.range(boardSize[1]).map(() => {
          return 0;
        });
      });

      this.highlightedBoardLetters = highlightedBoardLetters;
    },

    highlightBinLetterReset() {
      this.isHighlightedBinLetter = false;
    },

    highlightHoldLetterReset() {
      this.isHighlightedHoldLetter = false;
    },

    setTileHighlightQuery() {
      const query = this.$route.query.w;

      if (query) {
        const letters = query.split(",");
        letters.forEach((letter) => {
          this.highlightedBoardLetters[letter[0]][letter[1]] = 1;
        });
      }
    },

    isBonusTile(row, col) {
      let type = "";

      this.bonusTiles.forEach((bonus) => {
        if (bonus.pos && bonus.pos[0] === row && bonus.pos[1] === col) {
          type = bonus.type;
        }
      });

      return type;
    },

    async findWords(pos) {
      // pos = [1,2] row,col
      this.roundWords = [];

      const [row, col] = pos;

      // find all words on row that includes col
      const rows = this.boardSize[0];
      const columns = this.boardSize[1];
      const minWordLength = 2;
      const maxWordLength = 5;
      const columnsToCheck = columns - minWordLength + 1;
      const rowsToCheck = rows - minWordLength + 1;
      const tileRowIndex = row;
      const tileColIndex = col;

      let flagForRowRemoval = false;
      let flagForColumnRemoval = false;

      // check each column in row
      for (let columnIndex = 0; columnIndex < columnsToCheck; columnIndex++) {
        const possibleWords = columnsToCheck - columnIndex; // how many words can fit in space starting from column index

        // check for possible words starting at column index
        for (let wordCount = 0; wordCount < possibleWords; wordCount++) {
          // word length
          const wordLength = minWordLength + wordCount;

          let possibleWord = "";
          let value = 0;
          let indices = [];
          let wordBonus = [];

          // build the word
          for (let letter = columnIndex; letter < wordLength + columnIndex; letter++) {
            // check if letter is on a bonus tile
            let letterBonus = "";

            this.bonusTiles.forEach((bonus) => {
              if (bonus.pos.length && bonus.pos[0] === tileRowIndex && bonus.pos[1] === letter) {
                if (bonus.type === "dl" || bonus.type === "tl") {
                  letterBonus = bonus.type;
                }

                if (bonus.type === "tw" || bonus.type === "dw") {
                  wordBonus.push(bonus.type);
                }
              }
            });

            possibleWord += this.board[tileRowIndex][letter].letter || "_";
            indices.push([tileRowIndex, letter]);

            if (letterBonus === "dl") {
              value += this.board[tileRowIndex][letter].value * 2;
            } else if (letterBonus === "tl") {
              value += this.board[tileRowIndex][letter].value * 3;
            } else {
              value += this.board[tileRowIndex][letter].value;
            }
          }

          // check if word is valid
          let validWord = _.find(wordlist.words, (checkWord) => {
            return checkWord === possibleWord.toUpperCase() && possibleWord.length <= maxWordLength;
          });

          if (validWord) {
            if (wordBonus.length) {
              wordBonus.forEach((bonus) => {
                if (bonus === "tw") {
                  value = value * 3;
                }

                if (bonus === "dw") {
                  value = value * 2;
                }
              });
            }

            // check word length & add bonus
            if (validWord.length === 5) {
              value = value + 10;
              flagForRowRemoval = true;
            }

            if (validWord.length === 6) {
              value = value + 15;
            }

            if (validWord.length === 7) {
              value = value + 20;
            }

            // only add words that include the tile
            let isUsingTile = false;

            indices.forEach((index) => {
              if (index[1] === tileColIndex) {
                isUsingTile = true;
              }
            });

            if (isUsingTile) {
              this.roundWords.push({
                word: validWord,
                score: value,
                indices: indices,
              });
            }
          }
        }
      }

      // check each row in column
      for (let rowIndex = 0; rowIndex < rowsToCheck; rowIndex++) {
        const possibleWords = rowsToCheck - rowIndex; // how many words can fit in space starting from row index

        // check for possible words starting at row index
        for (let wordCount = 0; wordCount < possibleWords; wordCount++) {
          // word length
          const wordLength = minWordLength + wordCount;

          let possibleWord = "";
          let value = 0;
          let indices = [];
          let wordBonus = [];

          // build the word
          for (let letter = rowIndex; letter < wordLength + rowIndex; letter++) {
            // check if letter is on a bonus tile
            let letterBonus = "";

            this.bonusTiles.forEach((bonus) => {
              if (bonus.pos.length && bonus.pos[0] === letter && bonus.pos[1] === tileColIndex) {
                if (bonus.type === "dl" || bonus.type === "tl") {
                  letterBonus = bonus.type;
                }

                if (bonus.type === "tw" || bonus.type === "dw") {
                  wordBonus.push(bonus.type);
                }
              }
            });

            possibleWord += this.board[letter][tileColIndex].letter || "_";
            indices.push([letter, tileColIndex]);

            if (letterBonus === "dl") {
              value += this.board[letter][tileColIndex].value * 2;
            } else if (letterBonus === "tl") {
              value += this.board[letter][tileColIndex].value * 3;
            } else {
              value += this.board[letter][tileColIndex].value;
            }
          }

          let validWord = _.find(wordlist.words, (checkWord) => {
            return checkWord === possibleWord.toUpperCase() && possibleWord.length <= maxWordLength;
          });

          if (validWord) {
            if (wordBonus.length) {
              wordBonus.forEach((bonus) => {
                if (bonus === "tw") {
                  value = value * 3;
                }

                if (bonus === "dw") {
                  value = value * 2;
                }
              });
            }
            if (validWord.length === 5) {
              value = value + 10;
              flagForColumnRemoval = true;
            }

            if (validWord.length === 6) {
              value = value + 15;
            }

            if (validWord.length === 7) {
              value = value + 20;
            }

            // only add words that include the tile
            let isUsingTile = false;

            indices.forEach((index) => {
              if (index[0] === tileRowIndex) {
                isUsingTile = true;
              }
            });

            if (isUsingTile) {
              this.roundWords.push({
                word: validWord,
                score: value,
                indices: indices,
              });
            }
          }
        }
      }

      // console.info("this.roundWords", this.roundWords);

      // return this.roundWords;

      if (this.roundWords.length) {
        await this.showResults(this.roundWords);
      }

      console.info("showResults done");

      return "done";
    },

    async showResults(words) {
      await new Promise((resolve, reject) => {
        words.forEach((word, index) => {
          setTimeout(
            () => {
              this.displayResults.push(word);
              this.highlightBoardLetterReset([5, 5]);
              this.totalScore += word.score;

              let topCentre = 0;
              let leftCentre = 0;

              let firstTopCentre = 0;
              let firstLeftCentre = 0;
              let lastTopCentre = 0;
              let lastLeftCentre = 0;

              word.indices.forEach((letter, i) => {
                this.highlightLetter(letter[0], letter[1]);

                if (i === 0) {
                  const tile = this.$refs[`row${letter[0]}col${letter[1]}`][0];

                  // get tile position from the top on the document
                  firstTopCentre = tile.offsetTop;

                  // get centre of tile
                  const top = tile.getBoundingClientRect().top + window.scrollY;
                  const left = tile.getBoundingClientRect().left;
                  const height = tile.getBoundingClientRect().height;
                  const width = tile.getBoundingClientRect().width;

                  firstTopCentre = top + height / 2;
                  firstLeftCentre = left + width / 2;
                }

                if (i === word.indices.length - 1) {
                  const tile = this.$refs[`row${letter[0]}col${letter[1]}`][0];

                  // get centre of tile
                  const top = tile.getBoundingClientRect().top + window.scrollY;
                  const left = tile.getBoundingClientRect().left;
                  const height = tile.getBoundingClientRect().height;
                  const width = tile.getBoundingClientRect().width;

                  lastTopCentre = top + height / 2;
                  lastLeftCentre = left + width / 2;
                }
              });

              topCentre = (firstTopCentre + lastTopCentre) / 2;
              leftCentre = (firstLeftCentre + lastLeftCentre) / 2;

              this.showPoints(leftCentre, topCentre, word.score);

              this.$store.commit("endless/updateScore", word.score);

              this.currentScore = word.score;
            },
            500 + 1000 * index
          );

          setTimeout(
            () => {
              this.highlightBoardLetterReset([5, 5]);
              this.isScoringOver = true;
              this.currentScore = 0;
              resolve();
            },
            500 + 1000 * words.length
          );
        });
      });
    },

    showPoints(x, y, score) {
      var ComponentClass = Vue.extend(WordScore);
      var instance = new ComponentClass({
        propsData: {
          x: x,
          y: y,
          score: score,
        },
      });

      instance.$mount();
      document.querySelector("#app").appendChild(instance.$el);

      setTimeout(() => {
        instance.remove();
      }, 3000);
    },

    clickTile(event) {
      if (this.isGameOver) return;
      if (!this.bombArmed) return;
      if (!Number.isFinite(Number(this.bombs)) || Number(this.bombs) <= 0) {
        this.bombArmed = false;
        return;
      }

      const elements = document.elementsFromPoint(event.clientX, event.clientY);
      const tileEl = elements.find((el) => {
        if (!el || typeof el.getAttribute !== "function") return false;
        return el.getAttribute("type") === "board";
      });

      if (!tileEl) return;

      const row = parseInt(tileEl.getAttribute("row"), 10);
      const col = parseInt(tileEl.getAttribute("col"), 10);
      if (![row, col].every(Number.isInteger)) return;

      const slot = this.board[row][col];
      if (!slot || Object.keys(slot).length === 0) return;

      this.playBombSmoke(event.clientX, event.clientY);
      this.triggerBombShake();

      this.bombArmed = false;
      socket.emit("useBomb", {
        gameId: window.localStorage.getItem("gameId"),
        pos: [row, col],
      });
    },

    triggerBombShake() {
      if (this.bombShakeTimeoutId) clearTimeout(this.bombShakeTimeoutId);
      this.bombShakePulse = 30;
      this.bombShakeTimeoutId = setTimeout(() => {
        this.bombShakePulse = 0;
        this.bombShakeTimeoutId = null;
      }, 50);
    },

    playBombSmoke(clientX, clientY) {
      const burst = document.createElement("div");
      burst.className = "bomb-smoke-burst";
      burst.style.left = `${clientX}px`;
      burst.style.top = `${clientY}px`;

      const puffCount = 7;
      for (let i = 0; i < puffCount; i++) {
        const puff = document.createElement("span");
        puff.className = "bomb-smoke-puff";

        const angle = Math.random() * Math.PI * 2;
        const distance = 10 + Math.random() * 26;
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance - (10 + Math.random() * 22);
        const delay = Math.random() * 0.07;
        const scale = 0.9 + Math.random() * 0.9;

        puff.style.setProperty("--dx", `${dx}px`);
        puff.style.setProperty("--dy", `${dy}px`);
        puff.style.setProperty("--delay", `${delay}s`);
        puff.style.setProperty("--scale", `${scale}`);

        burst.appendChild(puff);
      }

      document.body.appendChild(burst);
      setTimeout(() => {
        if (burst.parentNode) burst.parentNode.removeChild(burst);
      }, 900);
    },

    toggleBombArmed() {
      if (this.isGameOver) {
        this.bombArmed = false;
        return;
      }
      if (!Number.isFinite(Number(this.bombs)) || Number(this.bombs) <= 0) return;
      this.bombArmed = !this.bombArmed;
    },
  },
};
</script>

<style lang="scss" scoped>
$tilesize: 5em;
$tilegap: 0.5em;
$tilecount: 5;
// $boardwidth: ($tilesize * $tilecount) + ($tilegap * $tilecount);

.section {
  align-items: center;
  display: flex;
  font-size: 0.75em;
  margin: auto;
  min-height: 100vh;
  justify-content: center;

  @media screen and (min-width: 480px) {
    font-size: 0.85em;
  }

  @media screen and (min-width: 768px) {
    font-size: 1em;
  }
}

.gameboard {
  padding: 12em 0 0;

  @media screen and (min-width: 768px) {
    padding: 8em 0 0;
  }

  &__top {
    display: flex;
    justify-content: center;
    margin: 0 auto 1em;
    position: relative;
    width: 20.25rem;

    @media screen and (min-width: 480px) {
      width: 23rem;
    }

    @media screen and (min-width: 768px) {
      width: 27rem;
    }
  }
}

.game-details {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  font-size: 1.25em;
  justify-content: space-between;
  margin: 0 0 0.6em;
  width: 100%;

  .date {
    color: #fff;
    font-size: 0.6em;
    margin: 6px 0 0;
  }
}

.bin {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &__heading {
    border-radius: 0.3em;
    color: #222;
    font-size: 0.75em;
    font-weight: 700;
    margin: 0 0 5px;
    padding: 0.5em 1.3em;
    text-transform: uppercase;
    width: max-content;
  }

  .tile {
    // background-color: #edf2f9;
    // background-image: url(../assets/trash.svg);
    // background-position: 50%;
    // background-repeat: no-repeat;
    box-shadow: inset 0 0 0.5em rgba(0, 0, 0, 0.2);
    overflow: hidden;

    &--locked {
      background-image: none;
    }

    .bin-filled {
      background-color: rgb(75, 147, 255);
      bottom: 0;
      height: 100%;
      pointer-events: none;
      position: absolute;
      user-select: none;
      width: 100%;
    }

    .bin-icon {
      background-image: url(../assets/trash.svg);
      background-position: 50%;
      background-repeat: no-repeat;
      height: 100%;
      position: relative;
      z-index: 1;
      pointer-events: none;
      user-select: none;
    }
  }
}

.hold {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &__heading {
    border-radius: 0.3em;
    color: #222;
    font-size: 0.75em;
    font-weight: 700;
    margin: 0 0 5px;
    padding: 0.5em 1.3em;
    text-transform: uppercase;
    width: max-content;
  }

  .tile {
    // background-image: url(../assets/hold.svg);
    // background-position: 50%;
    // background-repeat: no-repeat;
    // box-shadow: inset 0 0 0.5em rgba(0, 0, 0, 0.2);
    overflow: hidden;

    &--locked {
      background-image: none;
    }

    .hold-icon {
      background-image: url(../assets/hold.svg);
      background-position: 50%;
      background-repeat: no-repeat;
      height: 100%;
      position: relative;
      z-index: 1;
      pointer-events: none;
      user-select: none;
      box-shadow: inset 0 0 0.5em rgba(0, 0, 0, 0.2);
    }
  }
}

.tile-box {
  align-items: center;
  background: #fff;
  border-radius: 0.3em;
  // box-shadow: inset 0 0 0.5em rgba(0, 0, 0, 0.2);
  // display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0 0.25em 0.3em;
  width: 5.75em;

  &--preview {
    aspect-ratio: 52 / 68;
    position: absolute;
    top: -4em;
    left: 0;
    right: unset;
    font-size: 0.9em;
    padding: 0;
    width: 5.2em;

    @media screen and (min-width: 768px) {
      top: 50%;
      transform: translateY(-50%);
      bottom: unset;
      left: -$tilesize - $tilegap - 1.5em;
    }
  }

  &--preview {
    aspect-ratio: 52 / 68;
    position: absolute;
    top: -4em;
    left: 0;
    right: unset;
    font-size: 0.9em;
    padding: 0;
    width: 5.2em;

    @media screen and (min-width: 768px) {
      top: 50%;
      transform: translateY(-50%);
      bottom: unset;
      left: -$tilesize - $tilegap - 1.5em;
    }
  }

  &--bomb {
    .bomb-icon {
      background-image: url(../assets/bomb.svg);
      background-position: 50%;
      background-repeat: no-repeat;
      height: 100%;
      position: relative;
      z-index: 1;
      pointer-events: none;
      user-select: none;
      border-radius: 0.3em;
      box-shadow: inset 0 0 0.5em rgba(0, 0, 0, 0.2);

      &--armed {
        background-image: url(../assets/bomb-white.svg);
        background-color: rgba(236, 23, 23, 0.9);
        box-shadow:
          inset 0 0 0.15em rgba(255, 255, 255, 0.42),
          0px 4px 14px rgba(220, 38, 38, 0.4);
      }
    }
  }

  &__heading {
    border-radius: 0.3em;
    color: #222;
    font-size: 0.75em;
    font-weight: 700;
    margin: 0 0 0.1em;
    padding: 0.5em 0 0.25em;
    text-transform: uppercase;
    width: max-content;
  }
}

.board {
  display: grid;
  grid-column-gap: $tilegap;
  grid-row-gap: $tilegap;
  position: relative;

  &__find-words-btn {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 100;
  }

  .tile--bonus::before {
    align-items: center;
    border-radius: 50%;
    box-shadow: rgba(#000, 0.6) 0.1em 0.15em 0.15em;
    color: #fff;
    display: flex;
    font-size: 0.75em;
    justify-content: center;
    height: 2em;
    left: 0.25em;
    position: absolute;
    top: 0.25em;
    width: 2em;
  }

  .tile--bonus-dw::before {
    background: #10be4a;
    content: "DW";
  }

  .tile--bonus-tw::before {
    background: #ffac11;
    content: "TW";
  }

  .tile--bonus-tl::before {
    background: #a011ff;
    content: "TL";
  }

  .tile--bonus-dl::before {
    background: #1178ff;
    content: "DL";
  }
}

.bottom-row {
  display: flex;
  gap: 2em;
  justify-content: center;
  margin: 2em auto 0;
  position: relative;
  text-align: center;
  z-index: 10;
}

.score {
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 1.3em;
  margin: 0 0 0.75em;

  &__pill {
    border-radius: 20px;
    padding: 0.1em 0.8em 0.2em;
    user-select: none;
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.8);
  }

  &__help {
    background: rgba(0, 0, 0, 0.8);
    border: 1px solid #ffffff;
    border-radius: 20px;
    color: #ffffff;
    font-size: 0.8em;
    padding: 0.35em 0.8em 0.4em;
    text-transform: uppercase;
  }
}

.tile {
  background: rgba(255, 255, 255, 1);
  border-radius: 0.25em;
  // box-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
  box-sizing: border-box;
  cursor: grab;
  height: $tilesize;
  position: relative;
  width: $tilesize;

  &__letter {
    align-items: center;
    color: #222;
    display: flex;
    font-size: 2.4em;
    font-weight: 700;
    height: 100%;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 0;
    text-transform: uppercase;
    width: 100%;
  }

  &__value {
    bottom: 0.5em;
    color: #222;
    font-size: 0.9em;
    font-weight: 700;
    pointer-events: none;
    position: absolute;
    right: 0.5em;
  }

  &--highlight {
    background-color: rgb(75, 147, 255) !important;

    .tile__letter,
    .tile__value {
      color: #fff;
    }
  }

  &--slot {
    cursor: auto;
    user-select: none;
  }

  &--locked {
    pointer-events: none;
  }

  &--hover {
    background: #2962ff;
  }
}

.board--bomb-armed {
  .tile--locked {
    pointer-events: auto;
    cursor: pointer;
  }

  .tile--locked:hover {
    background-color: #d32f2f !important;

    .tile__letter,
    .tile__value {
      color: #fff;
    }
  }
}

.btn {
  appearance: none;
  border: none;
  border-radius: 0.25em;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 6px 16px;
  text-transform: uppercase;

  &--primary {
    background: rgba(41, 98, 255, 1);
    transition: background 200ms ease-in-out;

    &:hover {
      background: rgb(36, 86, 223);
    }
  }
}

.tile-wrapper {
  position: relative;

  &--bomb {
    aspect-ratio: 52 / 68;
    position: absolute;
    top: -4em;
    left: unset;
    right: 0;
    font-size: 0.9em;
    padding: 0;
    width: 5.2em;
    z-index: 1;

    @media screen and (min-width: 768px) {
      top: 50%;
      transform: translateY(-50%);
      bottom: unset;
      right: -$tilesize - $tilegap - 1.5em;
    }
  }

  .tile-box {
    width: 100%;
  }

  .bomb-alert {
    aspect-ratio: 8/5;
    background-color: rgba(250, 30, 30, 0.85);
    left: 50%;
    position: absolute;
    transform: translate(-50%, 10px);
    width: 30px;
    z-index: -1;
    border-radius: 10px 10px 0 0;
    transition: all 800ms ease-in-out;

    @media screen and (min-width: 768px) {
      width: 40px;
    }

    &--armed {
      animation: alert 2000ms linear infinite forwards;
      transform: translate(-50%, -18px);

      @media screen and (min-width: 768px) {
        transform: translate(-50%, -25px);
      }

      .light {
        animation: alert-pulse 2000ms linear infinite forwards;
      }
    }

    &::after {
      background-color: #333;
      bottom: 0;
      content: "";
      height: 4px;
      left: 0;
      position: absolute;
      width: 100%;

      @media screen and (min-width: 768px) {
        height: 5px;
      }
    }

    .light {
      aspect-ratio: 1;
      background-color: rgba(240, 255, 255, 0.9);
      border-radius: 50%;

      left: 50%;
      position: absolute;
      transform: translate(-2.5px, 5px);
      width: 5px;

      box-shadow:
        0 0 4px 2px rgba(255, 255, 255, 1),
        0 0 8px 5px rgba(250, 255, 255, 0.65),
        0 0 8px 15px rgba(250, 255, 255, 0.35);
      opacity: 0.2;

      @media screen and (min-width: 768px) {
        transform: translate(-3px, 7px);
        width: 6px;
      }
    }
  }
}

.result-total {
  border-top: 1px solid #fff;
  color: #fff;
  letter-spacing: 0.075em;
  padding: 0 20px;
  text-transform: uppercase;
}

.rotate {
  animation: rotate linear infinite 3s;
}

@keyframes rotate {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes alert {
  0% {
    box-shadow:
      inset 0 0 0.15em rgba(255, 255, 255, 0.1),
      0px -2px 8px 2px rgba(250, 230, 230, 0.1),
      0px -4px 12px 4px rgba(250, 30, 30, 0.1);
  }
  50% {
    box-shadow:
      inset 0 0 0.15em rgba(255, 255, 255, 0.42),
      0px -2px 8px 2px rgba(250, 230, 230, 0.5),
      0px -4px 12px 4px rgba(250, 30, 30, 0.25);
  }
  100% {
    box-shadow:
      inset 0 0 0.15em rgba(255, 255, 255, 0.1),
      0px -2px 8px 2px rgba(250, 230, 230, 0.1),
      0px -4px 12px 4px rgba(250, 30, 30, 0.1);
  }
}

@keyframes alert-pulse {
  0% {
    opacity: 0.2;
  }
  50% {
    box-shadow:
      0 0 2px 1px rgba(255, 255, 255, 0.95),
      0 0 8px 5px rgba(250, 255, 255, 0.65),
      0 0 8px 15px rgba(250, 255, 255, 0.35);
    opacity: 0.7;
  }
  100% {
    opacity: 0.2;
  }
}
</style>

<style lang="scss">
.bomb-smoke-burst {
  left: 0;
  pointer-events: none;
  position: fixed;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 9999;
}

.bomb-smoke-puff {
  animation: bombSmokePuff 0.75s ease-out forwards;
  animation-delay: var(--delay, 0s);
  background: rgba(180, 180, 180, 0.85);
  border-radius: 999px;
  filter: blur(6px);
  height: 24px;
  left: 0;
  position: absolute;
  top: 0;
  transform: translate(0, 0) scale(0.8);
  width: 24px;
}

.bomb-smoke-puff::before {
  background: inherit;
  border-radius: inherit;
  content: "";
  height: 100%;
  left: 40%;
  opacity: 0.85;
  position: absolute;
  top: 10%;
  width: 100%;
}

.bomb-smoke-puff::after {
  background: inherit;
  border-radius: inherit;
  content: "";
  height: 100%;
  left: -35%;
  opacity: 0.75;
  position: absolute;
  top: 25%;
  width: 100%;
}

@keyframes bombSmokePuff {
  0% {
    opacity: 0;
    transform: translate(0, 0) scale(0.5);
  }
  10% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translate(var(--dx, 0px), var(--dy, -24px)) scale(var(--scale, 1.4));
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Endless.vue